import {
  readScopedPresentationHint,
  scopedPresentationHintKey,
  writeScopedPresentationHint,
  type HintStorage,
} from '@/lib/scoped-presentation-hint';
import type { ThreadPanelLayoutHintScope } from '@/lib/thread-panel-layout-hint';

/**
 * Cold-load shape hint: what the thread's latest turn looked like last time
 * (top-anchored because it is taller than the column, with a files card,
 * with actions), so the transcript skeleton draws that turn instead of a
 * generic one and the content lands where the skeleton stood.
 *
 * Presentation only, like the panel hint: a few flags, scoped to identity +
 * workspace + thread route, never content. A stale hint costs one morph.
 */
export type ThreadTranscriptShape = {
  /** Taller than the column, so the turn sits at the top, not centred. */
  tall: boolean;
  card: boolean;
  actions: boolean;
};

/** On <html>: the tokens of the loading thread's shape (CSS reads `~=`). */
export const THREAD_SKELETON_SHAPE_ATTR = 'data-thread-skeleton';

const SHAPE_KEY_PREFIX = 'imai:thread-shape:v1';

export function threadTranscriptShapeKey(
  scope: ThreadPanelLayoutHintScope
): string | null {
  return scopedPresentationHintKey(SHAPE_KEY_PREFIX, [
    scope.identity,
    scope.orgSlug,
    scope.threadRouteRef,
  ]);
}

/** `known` marks a stored shape, so an empty-looking one still applies. */
export function encodeThreadTranscriptShape(
  shape: ThreadTranscriptShape
): string {
  return [
    'known',
    shape.tall ? 'tall' : null,
    shape.card ? 'card' : null,
    shape.actions ? 'actions' : null,
  ]
    .filter(Boolean)
    .join(' ');
}

export function readThreadTranscriptShapeHint(
  storage: HintStorage | null | undefined,
  scope: ThreadPanelLayoutHintScope
): string | null {
  return readScopedPresentationHint(storage, threadTranscriptShapeKey(scope));
}

export function writeThreadTranscriptShapeHint(
  storage: HintStorage | null | undefined,
  scope: ThreadPanelLayoutHintScope,
  shape: ThreadTranscriptShape
): void {
  writeScopedPresentationHint(
    storage,
    threadTranscriptShapeKey(scope),
    encodeThreadTranscriptShape(shape)
  );
}
